import Service, { service } from '@ember/service';

import { trackedFunction } from 'reactiveweb/function';

import { query } from 'umg-ember-backend/data/builders/umg-rest';
import { getRecordType } from 'umg-ember-backend/utils/get-record-type';

export default class RecordVariantsService extends Service {
  @service store;

  queryVariantsCache = new WeakMap();

  hasVariants(record) {
    const type = getRecordType(record);

    if (type === 'unknown' || !this.store.schema.hasResource({ type })) {
      return false;
    }

    return this.store.schema.fields({ type }).has('cmsVariantParentId');
  }

  variantParentId(record) {
    return this.hasVariants(record) ? (record.cmsVariantParentId ?? '0') : '0';
  }

  isParent(record) {
    return this.variantParentId(record) === '0';
  }

  isVariant(record) {
    return !this.isParent(record);
  }

  isChannelVariant(record) {
    return this.isVariant(record) && Boolean(record?.variantChannel);
  }

  isLanguageVariant(record) {
    return this.isVariant(record) && Boolean(record?.cmsLanguageId);
  }

  getVariants(record) {
    return this.queryVariants(record).value ?? [];
  }

  queryVariants(record) {
    if (!this.queryVariantsCache.has(record)) {
      this.queryVariantsCache.set(
        record,
        trackedFunction(this, async ({ isRetrying }) => {
          await Promise.resolve();
          let variantParentId = this.isParent(record)
            ? record.id
            : this.variantParentId(record);

          if (!variantParentId) {
            return [];
          }

          const queryObject = {
            'filter[cmsVariantParentId]': variantParentId,
            'sort[cmsLanguageId]': 'ASC',
          };

          if (this.isChannelVariant(record)) {
            queryObject['sort[vChannelId]'] = 'ASC';
          }

          const recordType = getRecordType(record);
          let { content } = await this.store.request(
            query(recordType, queryObject, {
              reload: isRetrying,
            }),
          );

          let variants = content.data ?? [];

          if (this.isVariant(record)) {
            variants = [...variants].filter((variant) => {
              return variant.id !== record.id;
            });

            const parent = this.store.peekRecord({
              type: recordType,
              id: String(this.variantParentId(record)),
            });

            variants.push(parent);
          }

          return variants;
        }),
      );
    }
    return this.queryVariantsCache.get(record);
  }

  reloadVariants(record) {
    return this.queryVariants(record).retry();
  }

  createVariant(record) {
    const variant = this.store.createRecord(getRecordType(record));

    variant.cmsVariantParentId = record.id;
    variant.cmsLanguageId = null;

    return variant;
  }
}
